import { createContext, useContext, useEffect, useState, ReactNode } from "react";

export type Lang = "he" | "ar";

type LanguageContextValue = {
  lang: Lang;
  setLang: (lang: Lang) => void;
  toggleLang: () => void;
};

const LanguageContext = createContext<LanguageContextValue | undefined>(undefined);

const STORAGE_KEY = "painur:lang";

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(() => {
    if (typeof window === "undefined") return "he";
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored === "ar" ? "ar" : "he";
  });

  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY, lang);
    document.documentElement.lang = lang;
    document.documentElement.dir = "rtl"; // both Hebrew and Arabic are RTL
  }, [lang]);

  const setLang = (l: Lang) => setLangState(l);
  const toggleLang = () => setLangState((prev) => (prev === "he" ? "ar" : "he"));

  return (
    <LanguageContext.Provider value={{ lang, setLang, toggleLang }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage must be used within LanguageProvider");
  return ctx;
}

// Translation dictionary for global UI strings
export const t = {
  // Navbar
  home: { he: "בית", ar: "الرئيسية" },
  services: { he: "טיפולים", ar: "العلاجات" },
  team: { he: "הצוות", ar: "الطاقم" },
  articles: { he: "מאמרים", ar: "مقالات" },
  about: { he: "אודות", ar: "من نحن" },
  contact: { he: "יצירת קשר", ar: "اتصلوا بنا" },
  bookAppointment: { he: "קביעת תור", ar: "حجز موعد" },
  menu: { he: "תפריט", ar: "القائمة" },
  // Footer
  navigation: { he: "ניווט", ar: "تنقل" },
  contactUs: { he: "צרו קשר", ar: "تواصلوا معنا" },
  followUs: { he: "עקבו אחרינו", ar: "تابعونا" },
  navigateWaze: { he: "נווט עם Waze", ar: "التنقل عبر Waze" },
  coursesTraining: { he: "קורסים והכשרות", ar: "دورات وتدريبات" },
  rightsReserved: { he: "כל הזכויות שמורות.", ar: "جميع الحقوق محفوظة." },
  builtWith: { he: "עוצב ונבנה ברמה הגבוהה ביותר ⚡", ar: "صُمم وبُني بأعلى مستوى ⚡" },
  // Language switcher
  switchToArabic: { he: "العربية", ar: "العربية" },
  switchToHebrew: { he: "עברית", ar: "עברית" },
} as const;

export function tr(key: keyof typeof t, lang: Lang): string {
  return t[key][lang];
}
